import { reactive, ref, shallowRef } from 'vue'

import { defineStore } from 'pinia'

import {
  isUsageDailyDateOutdated,
  withUsageDailyDayFailed,
  withUsageDailyDays,
} from '@/apiClient/queries/usageDailyCacheUtils'
import { adminQueryKeys } from '@/apiClient/queries/useAdminQueries'
import type { IUploadedFile } from '@/apiClient/types/file'
import type { IUsageDailyMetricsResponse } from '@/apiClient/types/metrics'
import { useMetricEvents } from '@/composables/useMetricEvents'
import createLogger from '@/lib/logger'
import { queryClient } from '@/lib/queryClient'
import { refreshKeycloakToken } from '@/main'
import { useAuthStore } from '@/store/auth'
import type {
  DailyMetricsPayload,
  FileGenerationJobPayload,
  FileGenerationJobProgressPayload,
  FileProcessingPayload,
  ImageGenerationJobPayload,
  SocketEventNotification,
} from '@/store/socketEvents/types'
import {
  FILE_GENERATION_JOBS,
  fileGenerationJobEvent,
  type FileGenerationJobPhase,
  type FileGenerationJobType,
  withDeliveredJobFile,
} from '@/utils/fileGenerationJobs'

import { useChatStore } from '../chat'
import { useFileStore } from '../file'
import { useToast } from '../toast'

const logger = createLogger('websockets')

/** Debounced reconnect delay for 1003 / 1012 / 1008 after refresh (ms). */
const SOCKET_RECONNECT_DEBOUNCE_MS = 1000

/** 1013 (Try Again Later): exponential backoff base and cap (ms). */
const SOCKET_1013_BACKOFF_BASE_MS = 2000
const SOCKET_1013_BACKOFF_MAX_MS = 60_000

export const useSocketEventsStore = defineStore('sockerEventStore', () => {
  const authStore = useAuthStore()

  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  /** Increments on each 1013 close; reset on successful `onopen`. */
  let socket1013BackoffAttempt = 0

  /** Websocket status tracker */
  const socketStatus = reactive({
    /** Tracks if the socket has been initialised */
    isInitialised: ref(false),
    /** Tracks if the socket has been authenticated */
    isAuthenticated: ref(false),
    /** Tracks if the socket is connected */
    isConnected: ref(false),
  })

  /** Current socket connection */
  const socket = shallowRef<WebSocket>()

  /** Send a message to the socket */
  const sendMessage = (payload: Record<string, unknown>) => {
    if (!socket.value) {
      logger.error('socket not initialised')
      return
    }
    socket.value.send(JSON.stringify(payload))
  }

  /**
   * Parse the message payload from a MessageEvent into a JSON value.
   * @throws {TypeError} If message data is not a string
   * @throws {SyntaxError} If JSON parsing fails
   */
  const parseMessagePayload = <T = unknown>(event: MessageEvent): T => {
    const raw = event.data
    if (typeof raw !== 'string') {
      throw new TypeError('WebSocket message must be text to parse as JSON')
    }
    try {
      return JSON.parse(raw) as T
    } catch (cause: unknown) {
      throw new SyntaxError('Failed to parse WebSocket message as JSON', {
        cause,
      })
    }
  }

  /** Apply a file-processing lifecycle update to the matching uploaded file (by file_id). */
  const handleFileProcessingUpdate = (payload: FileProcessingPayload) => {
    logger.log('File processing notification payload', payload)
    useFileStore().updateUploadedFile(payload.file_id, {
      status: payload.status,
      error: payload.error ?? undefined,
      name: payload.filename,
    })
  }

  type ScheduleReconnectOptions = {
    /** If true (e.g. close 1008 policy / auth), refresh Keycloak before reconnecting. */
    refreshToken?: boolean
    /** Delay before reconnect; defaults to {@link SOCKET_RECONNECT_DEBOUNCE_MS}. */
    delayMs?: number
    /** Log line for observability (close code context). */
    logContext: string
  }

  /**
   * Drop the closed socket and open a new connection after `delayMs`.
   */
  const scheduleSocketReconnect = ({
    refreshToken = false,
    delayMs = SOCKET_RECONNECT_DEBOUNCE_MS,
    logContext,
  }: ScheduleReconnectOptions) => {
    socket.value = undefined
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer)
    }
    logger.log(logContext)
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      void (async () => {
        if (refreshToken) {
          try {
            logger.log('Refreshing keycloak token and reconnecting socket')
            await refreshKeycloakToken()
          } catch {
            logger.log(
              'WebSocket reconnect skipped after token refresh failure',
            )
            return
          }
        }
        openSocket()
      })()
    }, delayMs)
  }

  /** Open a new socket connection */
  const openSocket = () => {
    socket.value = new WebSocket(import.meta.env.VITE_API_SOCKET_ENDPOINT)

    socket.value.onopen = () => {
      socketStatus.isConnected = true
      socket1013BackoffAttempt = 0
      sendMessage({ token: authStore.accessToken })
    }

    socket.value.onclose = (event) => {
      socketStatus.isConnected = false
      logger.log('Socket disconnected', event.code, event)

      // Server close codes we handle explicitly (see RFC 6455 / MDN CloseEvent.code).
      if (event.code === 1003) {
        // Unsupported / invalid first payload (e.g. not JSON object) — retry same flow.
        scheduleSocketReconnect({
          logContext:
            'Socket close 1003 (unsupported data) — debounced reconnect',
        })
      } else if (event.code === 1006) {
        // Abnormal closure: no close frame (network, proxy, sleep, TLS drop).
        // Not an auth signal — avoid refreshKeycloakToken() here (can trigger login / loops).
        scheduleSocketReconnect({
          logContext:
            'Socket close 1006 (abnormal closure) — debounced reconnect',
        })
      } else if (event.code === 1008) {
        // Policy violation — treat as auth/token failure per server contract.
        scheduleSocketReconnect({
          refreshToken: true,
          logContext:
            'Socket close 1008 (policy violation) — refresh token then reconnect',
        })
      } else if (event.code === 1012) {
        // Service restart — server going away temporarily.
        scheduleSocketReconnect({
          logContext:
            'Socket close 1012 (service restart) — debounced reconnect',
        })
      } else if (event.code === 1013) {
        // Try again later — e.g. Redis temporarily unavailable (server contract).
        const delayMs = Math.min(
          SOCKET_1013_BACKOFF_MAX_MS,
          SOCKET_1013_BACKOFF_BASE_MS * 2 ** socket1013BackoffAttempt,
        )
        socket1013BackoffAttempt += 1
        scheduleSocketReconnect({
          delayMs,
          logContext: `Socket close 1013 (try again later) — reconnect in ${delayMs}ms (backoff attempt ${socket1013BackoffAttempt})`,
        })
      }
    }

    socket.value.onerror = (event) => {
      logger.error('Socket error:', event)
    }

    socket.value.onmessage = (event) => {
      logger.log('Socket message received', event)

      const parsed = parseMessagePayload(event)

      const top = parsed as { event?: unknown }
      if (top.event !== 'notification') {
        logger.log(`Unhandled event: ${String(top.event ?? 'unknown')}`)
        return
      }

      const socketNotification = parsed as SocketEventNotification<unknown>
      const notificationObj = socketNotification.notification

      logger.log('Notification object', notificationObj)

      const fileGenerationEvent = fileGenerationJobEvent(
        notificationObj?.type ?? '',
      )
      if (fileGenerationEvent) {
        handleFileGenerationJobNotification(
          fileGenerationEvent.jobType,
          fileGenerationEvent.phase,
          notificationObj.payload,
        )
        return
      }

      switch (notificationObj?.type) {
        case 'file_processing_started':
        case 'file_processing_succeeded':
        case 'file_processing_failed':
        case 'file_processing_retrying':
        case 'file_processing_verifying':
          handleFileProcessingUpdate(
            notificationObj.payload as FileProcessingPayload,
          )
          break

        case 'image_generation_job_succeeded':
          handleImageGenerationJobSucceeded(
            notificationObj.payload as ImageGenerationJobPayload,
          )
          break
        case 'image_generation_job_failed':
          handleImageGenerationJobFailed(
            notificationObj.payload as ImageGenerationJobPayload,
          )
          break
        case 'daily_metrics_computed':
          handleDailyMetricsComputed(
            notificationObj.payload as DailyMetricsPayload,
          )
          break

        case 'daily_metrics_failed':
          handleDailyMetricsFailed(
            notificationObj.payload as DailyMetricsPayload,
          )
          break

        default:
          logger.log(
            `Unhandled event: ${String(notificationObj?.type ?? 'unknown')}`,
          )
      }
    }
  }

  /** Initialise the socket events */
  const initSocketEvents = () => {
    logger.log('initSocketEvents')
    if (!import.meta.env.VITE_API_SOCKET_ENDPOINT) {
      logger.log('No socket endpoint configured')
      return
    }
    if (socket.value) {
      logger.log('socket already initialised')
      return
    }

    openSocket()
  }

  /** The files an assistant reply shows now, as the chat store holds them. */
  const replyFiles = (chatId: string, messageId: string) =>
    useChatStore()
      .getChatById(chatId)
      ?.messages.find((message) => message.id === messageId)?.files

  // TODO: find a better place for this:
  const handleImageGenerationJobSucceeded = (
    payload: ImageGenerationJobPayload,
  ) => {
    logger.log('Image generation job succeeded', payload)
    // No URL means nothing can render: the file would still retire the job and
    // pass the `image/` content-type check, leaving a shimmer with no job behind
    // it. Bail the way the file-generation siblings do. (`||` not `??`: '' is as
    // useless.)
    if (!payload.url && !payload.signed_url) {
      logger.error('Image generation job succeeded without a URL', payload)
      useChatStore().clearImageGenerationJob(
        payload.chat_id,
        payload.message_id,
      )
      useToast().onError('Generated image could not be loaded')
      return
    }

    // The event carries real file metadata, same shape as the file-generation jobs.
    const file: IUploadedFile = {
      id: payload.file_id,
      name: payload.file_name || payload.file_id,
      content_type: payload.file_content_type || 'image/',
      size: payload.file_size ?? 0,
      status: 'processed', // Not needed
      url: payload.url,
      signed_url: payload.signed_url,
      error: undefined,
    }

    // Add file to the chat message, next to what the reply's other jobs delivered.
    useChatStore().setChatResponseMessage({
      chat_id: payload.chat_id,
      message_id: payload.message_id,
      content: '',
      model: 'image-generation', // Not needed, doesn't override
      timestamp: Date.now(),
      files: withDeliveredJobFile(
        replyFiles(payload.chat_id, payload.message_id),
        file,
      ),
    })
    useChatStore().clearImageGenerationJob(payload.chat_id, payload.message_id)
  }

  const handleImageGenerationJobFailed = (
    payload: ImageGenerationJobPayload,
  ) => {
    logger.log('Image generation job failed', payload)
    useChatStore().clearImageGenerationJob(payload.chat_id, payload.message_id)
    useToast().onError(payload.error || 'Image generation job failed')
  }

  /** Routes a `FILE_GENERATION_JOBS` websocket event to its phase handler. */
  const handleFileGenerationJobNotification = (
    jobType: FileGenerationJobType,
    phase: FileGenerationJobPhase,
    payload: unknown,
  ) => {
    switch (phase) {
      case 'progress':
        handleFileGenerationJobProgress(
          payload as FileGenerationJobProgressPayload,
        )
        break
      case 'succeeded':
        handleFileGenerationJobSucceeded(
          jobType,
          payload as FileGenerationJobPayload,
        )
        break
      case 'failed':
        handleFileGenerationJobFailed(
          jobType,
          payload as FileGenerationJobPayload,
        )
        break
    }
  }

  const handleFileGenerationJobProgress = (
    payload: FileGenerationJobProgressPayload,
  ) => {
    logger.log('File generation job progress', payload)
    if (!payload.ui_hints) return
    useChatStore().setChatResponseMessage({
      chat_id: payload.chat_id,
      message_id: payload.message_id,
      ui_hints: payload.ui_hints,
    })
  }

  const handleFileGenerationJobSucceeded = (
    jobType: FileGenerationJobType,
    payload: FileGenerationJobPayload,
  ) => {
    const { logLabel, generatedMetricEvent } = FILE_GENERATION_JOBS[jobType]
    logger.log(`${logLabel} job succeeded`, payload)
    if (!payload.file_id || !payload.file_name || !payload.file_content_type) {
      logger.error(
        `${logLabel} job succeeded but file metadata is missing`,
        payload,
      )
      useChatStore().clearFileGenerationPlaceholder(
        payload.chat_id,
        payload.message_id,
        jobType,
      )
      return
    }
    useMetricEvents().sendEvent(generatedMetricEvent)
    const file: IUploadedFile = {
      id: payload.file_id,
      name: payload.file_name,
      content_type: payload.file_content_type,
      size: payload.file_size ?? 0,
      status: 'processed',
      url: payload.url ?? undefined,
      signed_url: payload.signed_url ?? undefined,
    }
    // Leave the reply as a reload shows it: its text and the files its other jobs delivered
    // stay, this job's placeholder gives way to the file, and only this job's progress goes.
    const deliveryMessage = payload.delivery_message
    // An API that does not send the delivery message yet: its text goes on the reply.
    const contentReplace =
      !deliveryMessage &&
      typeof payload.content === 'string' &&
      payload.content.length > 0
        ? payload.content
        : undefined
    useChatStore().setChatResponseMessage({
      chat_id: payload.chat_id,
      message_id: payload.message_id,
      content_replace: contentReplace,
      files: withDeliveredJobFile(
        replyFiles(payload.chat_id, payload.message_id),
        file,
        jobType,
      ),
      clear_processing_step: FILE_GENERATION_JOBS[jobType].progressStep,
    })
    // The delivery text ("Your presentation is ready…") is a message of its own in the chat.
    if (deliveryMessage) {
      useChatStore().appendChatMessage(payload.chat_id, deliveryMessage)
    }
  }

  const handleFileGenerationJobFailed = (
    jobType: FileGenerationJobType,
    payload: FileGenerationJobPayload,
  ) => {
    const { logLabel, failureMessage } = FILE_GENERATION_JOBS[jobType]
    logger.log(`${logLabel} job failed`, payload)
    useChatStore().clearFileGenerationPlaceholder(
      payload.chat_id,
      payload.message_id,
      jobType,
    )
    useChatStore().markFileGenerationJobFailed(
      payload.chat_id,
      payload.message_id,
      jobType,
      payload.error,
    )
    useToast().onError(payload.error || failureMessage)
  }

  // #region Daily usage metrics
  /** Cached usage-daily responses in the payload's scope (all users, or one employee). */
  const usageDailyQueriesFor = (employeeId: string | undefined) =>
    queryClient
      .getQueriesData<IUsageDailyMetricsResponse>({
        queryKey: [...adminQueryKeys.all, 'usageDaily'],
      })
      .flatMap(([queryKey, data]) =>
        data && (data.employee_id ?? undefined) === employeeId
          ? [{ queryKey, data }]
          : [],
      )

  /** When each cached usage-daily query last refetched for a date, by query and date. */
  const usageDailyRefetchedAt = new Map<string, number>()

  const handleDailyMetricsComputed = (payload: DailyMetricsPayload) => {
    for (const { queryKey, data } of usageDailyQueriesFor(
      payload.employee_id,
    )) {
      // Employee-scoped days are not cached server-side, so a refetch would compute again.
      if (payload.day_data) {
        queryClient.setQueryData(
          queryKey,
          withUsageDailyDays(data, [payload.day_data]),
        )
      } else {
        const refetchKey = `${JSON.stringify(queryKey)}:${payload.bucket_date}`
        if (
          isUsageDailyDateOutdated(
            data,
            payload.bucket_date,
            usageDailyRefetchedAt.get(refetchKey),
          )
        ) {
          usageDailyRefetchedAt.set(refetchKey, Date.now())
          queryClient.invalidateQueries({ queryKey, exact: true })
        }
      }
    }
  }

  const handleDailyMetricsFailed = (payload: DailyMetricsPayload) => {
    logger.log('Daily metrics computation failed', payload)
    for (const { queryKey, data } of usageDailyQueriesFor(
      payload.employee_id,
    )) {
      queryClient.setQueryData(
        queryKey,
        withUsageDailyDayFailed(data, payload.bucket_date),
      )
    }
  }
  // #endregion

  return {
    socket,
    initSocketEvents,
  }
})
